/**
 * Console Monkey Patch Utility
 *
 * This utility patches console methods to control logging based on a URL query parameter.
 * If 'debug=true' or 'enableLogs=true' is present in the URL, console logs are enabled
 * and the setting is persisted in session storage.
 */

import { getSessionStorage } from './enhancedSessionStorage'

const DEBUG_QUERY_PARAMS = ['debug'] as const
const SESSION_STORAGE_KEY = 'quince_console_debug_enabled'

// Store original console methods
export const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
  trace: console.trace,
  dir: console.dir,
  table: console.table,
  group: console.group,
  groupCollapsed: console.groupCollapsed,
  groupEnd: console.groupEnd,
  time: console.time,
  timeEnd: console.timeEnd,
  count: console.count,
  assert: console.assert,
}

/* eslint-disable @typescript-eslint/no-empty-function */
// Noop functions to replace console methods when disabled
const noopFunctions = {
  log: () => {},
  warn: originalConsole.warn, // Keep warn logs even when disabled
  error: originalConsole.error, // Keep error logs even when disabled
  info: () => {},
  debug: () => {},
  trace: () => {},
  dir: () => {},
  table: () => {},
  group: () => {},
  groupCollapsed: () => {},
  groupEnd: () => {},
  time: () => {},
  timeEnd: () => {},
  count: () => {},
  assert: () => {},
}
/* eslint-enable @typescript-eslint/no-empty-function */

/**
 * Check if console logging should be enabled based on query parameters
 */
function shouldEnableConsoleFromQuery(): boolean {
  if (typeof window === 'undefined') return false

  const urlParams = new URLSearchParams(window.location.search)

  return DEBUG_QUERY_PARAMS.some(param => {
    const value = urlParams.get(param)
    return value === 'true'
  })
}

/**
 * Check if console logging is enabled from session storage
 */
function isConsoleEnabledFromStorage(): boolean {
  if (typeof window === 'undefined') return false

  try {
    const stored = getSessionStorage().getItem<string>(SESSION_STORAGE_KEY, { isGlobal: true })
    return stored === 'true'
  } catch (e) {
    return false
  }
}

/**
 * Store console enabled state in session storage
 */
function storeConsoleEnabledState(enabled: boolean): void {
  if (typeof window === 'undefined') return

  try {
    getSessionStorage().setItem(SESSION_STORAGE_KEY, enabled.toString(), { isGlobal: true })
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Apply console monkey patch based on enabled state
 */
function applyConsolePatch(enabled: boolean): void {
  const consoleMethods = enabled ? originalConsole : noopFunctions

  Object.assign(console, consoleMethods)

  // Add a visual indicator that console patching is active
  if (enabled) {
    originalConsole.log(
      '%c[Debug Mode Enabled]%c Console logs are now enabled via query parameter',
      'color: #00ff00; font-weight: bold; background: #000; padding: 2px 4px; border-radius: 2px;',
      'color: #888; font-style: italic;',
    )
  }
}

/**
 * Initialize console monkey patch
 * This should be called early in the client initialization
 */
export function initializeConsoleMonkeyPatch(isProdBuild = true): void {
  if (typeof window === 'undefined' || !isProdBuild) return

  // Check if console should be enabled from query params
  const enableFromQuery = shouldEnableConsoleFromQuery()

  // Check if console is already enabled from session storage
  const enableFromStorage = isConsoleEnabledFromStorage()

  // Determine final enabled state
  let shouldEnable = enableFromQuery || enableFromStorage

  // If query param is present, update session storage
  if (enableFromQuery) {
    storeConsoleEnabledState(true)
    shouldEnable = true
  }

  // Apply the monkey patch
  applyConsolePatch(shouldEnable)
}
